function toDepth(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

function toGrade(row) {
  const n = parseFloat(row?.Au_ppm ?? row?.Au_gpt ?? row?.GRADE ?? row?.value);
  return Number.isFinite(n) ? n : null;
}

function assayIntervals(assays = []) {
  return (assays || [])
    .map((row) => {
      const from = toDepth(row.Depth_From);
      const to = toDepth(row.Depth_To);
      const grade = toGrade(row);
      if (from == null || to == null || to <= from || grade == null) return null;
      return { from, to, grade };
    })
    .filter(Boolean)
    .sort((a, b) => a.from - b.from || a.to - b.to);
}

/**
 * Length-weighted composites onto a regular downhole step (default 1 m).
 */
export function compositeAssays(assays = [], step = 1) {
  const size = Number(step);
  if (!(size > 0)) return [];
  const intervals = assayIntervals(assays);
  if (!intervals.length) return [];
  const start = Math.floor(intervals[0].from / size) * size;
  const end = intervals[intervals.length - 1].to;
  const out = [];
  for (let from = start; from < end - 1e-6; from += size) {
    const to = from + size;
    let num = 0;
    let den = 0;
    intervals.forEach((iv) => {
      const a = Math.max(from, iv.from);
      const b = Math.min(to, iv.to);
      const len = b - a;
      if (len <= 0) return;
      num += iv.grade * len;
      den += len;
    });
    if (den <= 0) continue;
    out.push({
      from,
      to,
      value: num / den,
      label: `${(num / den).toFixed(2)} g/t`,
      covered: den,
    });
  }
  return out;
}

/**
 * Highest-grade window of at least minWidth metres, mean grade ≥ cutoff.
 * maxWaste is unused waste length allowed inside the window (metres).
 */
export function bestInterval(assays = [], { minWidth = 3, cutoff = 0.6, maxWaste = 1 } = {}) {
  const intervals = assayIntervals(assays);
  if (!intervals.length) return null;
  const minW = Math.max(0.1, Number(minWidth) || 3);
  const cut = Number.isFinite(Number(cutoff)) ? Number(cutoff) : 0.6;
  const wasteCap = Math.max(0, Number(maxWaste) || 0);
  let best = null;

  for (let i = 0; i < intervals.length; i += 1) {
    let metal = 0;
    let length = 0;
    let waste = 0;
    for (let j = i; j < intervals.length; j += 1) {
      const iv = intervals[j];
      const len = iv.to - iv.from;
      metal += iv.grade * len;
      length += len;
      if (iv.grade < cut) waste += len;
      if (waste > wasteCap + 1e-6) break;
      if (length + 1e-6 < minW) continue;
      const grade = metal / length;
      if (grade < cut) continue;
      if (
        !best ||
        grade > best.grade + 1e-6 ||
        (Math.abs(grade - best.grade) <= 1e-6 && length > best.length)
      ) {
        best = {
          from: intervals[i].from,
          to: iv.to,
          length,
          grade,
          label: `${length.toFixed(1)} m @ ${grade.toFixed(2)} g/t`,
        };
      }
    }
  }
  return best;
}
